<div class="container my-4" *transloco="let t; scope: 'ideas'">
  @if (failure(); as failed) {
    <div class="alert alert-warning" role="alert">{{ t(failed.translationKey) }}</div>
  } @else if (loading() && !idea()) {
    <multisite-loader-globe-animation-detail [width]="100" [height]="100"></multisite-loader-globe-animation-detail>
  } @else if (idea(); as current) {
    <div class="row">
      <div class="col-lg-8">
        <p class="mb-2">
          @if (current.pending) {
            <span class="badge text-bg-warning me-2">{{ t('ideas.relevance.awaiting_approval') }}</span>
          } @else {
            <span class="badge me-2" [class]="stageBadge(current.stage)">{{ t(statusKey(current.status)) }}</span>
          }
          @if (current.visibility === 'hub') {
            <span class="badge text-bg-light border me-2"><i class="fa-regular fa-lock me-1"></i>{{ t('ideas.private_to', { hub: current.hub?.name ?? '' }) }}</span>
          }
          @if (current.platformWide) {
            <span class="badge text-bg-light border me-2">{{ t('common.platform_wide') }}</span>
          }
          @if (current.statusChangedAt) {
            <small class="text-muted">{{ current.statusChangedAt | date: 'mediumDate' }}</small>
          }
        </p>

        @if (current.topics.length || current.tags.length || current.cultures.length) {
          <p class="mb-3">
            @for (topic of current.topics; track topic.slug) {
              <span class="badge text-bg-light border me-1">{{ topic.name }}</span>
            }
            @for (tag of current.tags; track tag.slug) {
              <a class="badge text-bg-light border me-1 text-decoration-none" [routerLink]="baseRoute" [queryParams]="{ tag: tag.slug }">
                <i class="fa-solid fa-tag me-1" aria-hidden="true"></i>{{ tag.name }}
              </a>
            }
            @for (culture of current.cultures; track culture.slug) {
              <span class="badge text-bg-light border me-1">{{ culture.name }}</span>
            }
          </p>
        }

        <!-- The summary is what the idea is, in a sentence or two: the first thing to read. -->
        @if (current.summary) {
          <p class="lead fs-4 fw-normal border-start border-4 border-primary ps-3 my-4">{{ current.summary }}</p>
        }

        @if (current.pending) {
          <div class="alert alert-info">{{ t('ideas.pending_explanation') }}</div>
        }

        @if (current.statusReason) {
          <div class="alert alert-secondary"><strong>{{ t('ideas.our_answer') }}</strong> {{ current.statusReason }}</div>
        }
        @if (current.duplicateOf) {
          <p><a [routerLink]="[...baseRoute, current.duplicateOf]">{{ t('ideas.see_the_original') }}</a></p>
        }
        @if (current.followsOnFrom) {
          <p class="small"><a [routerLink]="[...baseRoute, current.followsOnFrom]">{{ t('ideas.follows_on_from') }}</a></p>
        }
        @if (translating()) {
          <div class="d-flex align-items-center gap-2 my-2" role="status">
            <multisite-loader-globe-animation-detail [width]="40" [height]="40"></multisite-loader-globe-animation-detail>
            <span>{{ t('news.translating') }}</span>
          </div>
        } @else if (current.servedLocale && current.requestedLocale && current.servedLocale !== current.requestedLocale) {
          <p class="small text-muted">{{ t('ideas.served_in', { locale: current.servedLocale }) }}</p>
        }

        @if (current.description) {
          <h2 class="h5 mt-4">{{ t('ideas.proposal') }}</h2>
          <div [innerHTML]="current.description | insightMarkdown"></div>
        }

        <h2 class="h5 mt-4">{{ t('ideas.in_their_words') }}</h2>
        <blockquote class="border-start ps-3" style="white-space: pre-line">{{ current.originalRequest }}</blockquote>
        @if (current.origin !== 'self_service') {
          <p class="small text-muted">{{ t('ideas.origin.' + current.origin) }}</p>
        } @else if (current.initiatorName) {
          <p class="small text-muted">{{ t('ideas.asked_by', { who: current.initiatorName }) }}</p>
        }

        @if (current.solution) {
          <h2 class="h5 mt-4">{{ t('ideas.what_we_delivered') }}</h2>
          <div [innerHTML]="current.solution | insightMarkdown"></div>
        }

        @if (actionFailure(); as failed) {
          <div class="alert alert-danger mt-3" role="alert">{{ t(failed.translationKey) }}</div>
        }

        @if (current.can.withdraw) {
          <div class="card mt-4">
            <div class="card-body">
              <p class="mb-2">{{ t('ideas.withdraw_explanation') }}</p>
              @if (!confirmingWithdrawal()) {
                <button type="button" class="btn btn-outline-danger btn-sm" (click)="confirmingWithdrawal.set(true)">{{ t('common.withdraw') }}</button>
              } @else {
                <button type="button" class="btn btn-danger btn-sm me-2" [disabled]="withdrawing()" (click)="withdraw(false)">{{ t('common.withdraw_confirm') }}</button>
                <button type="button" class="btn btn-primary btn-sm me-2" [disabled]="withdrawing()" (click)="withdraw(true)">{{ t('ideas.withdraw_and_start_again') }}</button>
                <button type="button" class="btn btn-link btn-sm" (click)="confirmingWithdrawal.set(false)">{{ t('common.cancel') }}</button>
              }
            </div>
          </div>
        }

        @if (!current.pending) {
          <div class="mt-5">
            <comments-post-display-pagination-all-in-one
              [user]="user() ?? undefined"
              [hasPermissionToReadComments]="true"
              [hasPermissionToPostComments]="current.can.comment"
              [disabled]="!current.can.comment"
              [paginatedComments]="paginatedComments()"
              [loadingComments]="loadingComments()"
              [clearCommentForm]="clearCommentForm()"
              [titleText]="conversationName()"
              [titleClasses]="'h5'"
              [titleTranslationKey]="'social.leave_a_comment'"
              [descriptionTranslationKey]="'ideas.comments_in_your_language'"
              [descriptionTranslationScope]="'ideas'"
              (postComment)="postComment($event)"
              (getComments)="getComments($event)">
            </comments-post-display-pagination-all-in-one>
          </div>
        }
      </div>

      <div class="col-lg-4">
        @if (current.inputs.length) {
          <div class="card mb-3 border-primary">
            <div class="card-body">
              <h2 class="h6">{{ t('ideas.i_want_to_help') }}</h2>
              <p class="small text-muted">{{ t('ideas.help_intro') }}</p>
              <ul class="list-unstyled mb-0">
                @for (offer of current.inputs; track offer.slug) {
                  <li class="mb-2">
                    <a [routerLink]="['/contributions/inputs', offer.slug]" [queryParams]="{ from: current.slug }">{{ offer.name }}</a>
                    @if (offer.task) {
                      <div class="small text-muted">{{ offer.task }}</div>
                    }
                  </li>
                }
              </ul>
            </div>
          </div>
        }
        @if (!current.pending) {
          <div class="card">
            <div class="card-body d-flex justify-content-between align-items-center">
              <button type="button" class="btn btn-sm" [class.btn-primary]="current.liked" [class.btn-outline-primary]="!current.liked"
                [disabled]="!current.can.like || liking()" (click)="toggleLike()" [attr.aria-pressed]="current.liked">
                <i class="fa-regular fa-thumbs-up me-1"></i>{{ current.liked ? t('ideas.you_like_this') : t('ideas.like') }}
              </button>
              <span class="small text-muted">{{ t('ideas.relevance.people_want_this', { count: current.likesCount }) }}</span>
            </div>
          </div>
        }
        @if (current.can.follow) {
          <div class="card mt-3">
            <div class="card-body">
              <button type="button" class="btn btn-sm" [class.btn-primary]="current.following" [class.btn-outline-primary]="!current.following"
                [disabled]="changingFollow()" (click)="toggleFollow()" [attr.aria-pressed]="current.following" aria-describedby="idea-follow-help">
                <i class="fa-regular fa-bell me-1" aria-hidden="true"></i>{{ current.following ? t('ideas.following') : t('ideas.follow') }}
              </button>
              <p id="idea-follow-help" class="small text-muted mt-2 mb-0">{{ t('ideas.follow_help') }}</p>
            </div>
          </div>
        }
      </div>
    </div>
  }
</div>
